import React from 'react'

import { InfoIcon } from 'lucide-react'

import { getErrorMessage } from 'utils'

import type { CertificateAdmin } from 'types'

import { useSnackbar } from 'contexts'
import { useCertificateGroupUnlink } from 'hooks/api'

import { Stack, Typography } from '@mui/material'

import { Modal, Button, Alert } from '@repo/ui'
import { CertificatePreview, CertificateGroupMemberPreview, GroupMemberList } from 'components'

interface SplitGroupModalProps {
  /** The group's parent certificate — the one shown in the confirmation preview. */
  certificate: CertificateAdmin | null
  /** Every child in the group (full records, not just ids) — rendered below the
   *  parent using the same tree graphic as the certificate detail page's
   *  "Children" section (VRG-231 review round 2 item 8, §36). A split unlinks
   *  all of them at once. */
  children: CertificateAdmin[]
  open: boolean
  onClose: () => void
  /** Called after a successful split (e.g. refetch the page + group data). */
  onSplit: () => void
}

// VRG-235 — "Split group": only ever offered on a DIVISIBLE parent (the Actions
// menu entry is absent entirely for indivisible groups — see CertificatePage's
// getActionItems). Unlinks every child from the parent in one call; the parent
// certificate itself survives as a standalone (once again tagless) certificate,
// and every former child survives as its own standalone certificate.
export const SplitGroupModal = (props: SplitGroupModalProps) => {
  const { certificate, children, open, onClose, onSplit } = props
  const childIds = children.map(child => child.id)

  const { open: notify } = useSnackbar()

  const { unlinkCertificateGroup, isUnlinking } = useCertificateGroupUnlink({
    onSuccess: () => {
      notify({ type: 'success', title: 'Certificate group split' })
      onClose()
      onSplit()
    },
    onError: (error) => {
      notify({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const handleSplit = () => {
    if (!childIds.length) return
    // The path certificateId is only a fallback when the body's childIds is
    // empty (see certificates.admin.controller.ts) — any member id works here.
    unlinkCertificateGroup({ certificateId: childIds[0], childIds })
  }

  return (
    <Modal
      title="Split group"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={onClose}
            disabled={isUnlinking}
          >
            Cancel
          </Button>
          {/* VRG-231 review round 2 item 7 — NOT destructive: splitting a group
              destroys no record, it only severs a relationship (every member
              survives), so it doesn't earn the loud red confirm treatment
              (§32/§38) that a real delete gets. */}
          <Button
            disableElevation
            onClick={handleSplit}
            disabled={isUnlinking || !childIds.length}
          >
            {isUnlinking ? 'Splitting...' : 'Split'}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Are you sure you want to split the following certificate group?
        </Typography>
        <CertificatePreview certificate={certificate} size="medium" />
        {/* VRG-231 review round 2 item 8 — the children render below the parent
            using the SAME tree/indent graphic as CertificatePage's "Children"
            section (GroupMemberList, §36), not a second hand-rolled tree. */}
        {children.length > 0 && (
          <GroupMemberList>
            {children.map(child => (
              <CertificateGroupMemberPreview key={child.id} certificate={child} role="child" size={48} />
            ))}
          </GroupMemberList>
        )}
        <Alert
          severity="info"
          icon={<InfoIcon size={18} strokeWidth={1} />}
          description="If you split a certificate group, all children will be stored as separate certificates."
        />
      </Stack>
    </Modal>
  )
}
